import { type ReactNode } from "react";
import { Search, Download, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Field = {
  label: string;
  required?: boolean;
  /** The actual input/select element as a render slot. */
  control: ReactNode;
};

type Props = {
  title?: string;
  /** Exactly 4 fields like OLD (2 rows × 2 cols). Other counts still render in a grid. */
  fields: Field[];
  onSubmit: () => void;
  onExport?: () => void;
  extra?: ReactNode;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
};

export function SearchPanel({
  title = "Thông tin tìm kiếm",
  fields,
  onSubmit,
  onExport,
  extra,
  collapsible,
  collapsed,
  onToggle,
}: Props) {
  return (
    <div className="mb-4 rounded-lg border border-border bg-background">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-sm uppercase tracking-wider">{title}</span>
          {extra}
        </div>
        {collapsible && (
          <button
            type="button"
            onClick={onToggle}
            className="h-6 w-6 grid place-items-center rounded hover:bg-muted"
            aria-label={collapsed ? "Mở rộng" : "Thu gọn"}
          >
            <Minus className="h-4 w-4" />
          </button>
        )}
      </div>
      {!collapsed && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="p-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {fields.map((f, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:items-center gap-1.5 sm:gap-3"
                >
                  <label
                    className={cn(
                      "text-sm text-muted-foreground",
                      f.required && "after:content-['*'] after:text-destructive after:ml-1",
                    )}
                  >
                    {f.label}
                  </label>
                  <div className="min-w-0">{f.control}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
              <Button type="submit" variant="default" size="sm">
                <Search className="h-4 w-4 mr-1.5" /> Tìm kiếm
              </Button>
              {onExport && (
                <Button type="button" variant="outline" size="sm" onClick={onExport}>
                  <Download className="h-4 w-4 mr-1.5" /> Xuất báo cáo
                </Button>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

/** Shared input/select styles to keep search fields visually cohesive. */
export const filterInputClass =
  "w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
